import { Injectable } from '@angular/core';

export const LOCATION_SEARCH_HISTORY_STORAGE_KEY = 'REALTY_LOCATION_SEARCH_HISTORY';

const LOCATION_SEARCH_HISTORY_LIMIT = 20;

export interface LocationSearchHistoryItem {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocationSearchHistoryService {
  getRecent(): LocationSearchHistoryItem[] {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const stored = window.localStorage.getItem(LOCATION_SEARCH_HISTORY_STORAGE_KEY);
      const parsed: unknown = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .map((item) => this.normalizeItem(item))
        .filter((item): item is LocationSearchHistoryItem => item !== null)
        .slice(0, LOCATION_SEARCH_HISTORY_LIMIT);
    } catch {
      return [];
    }
  }

  save(item: LocationSearchHistoryItem): LocationSearchHistoryItem[] {
    const normalized = this.normalizeItem(item);
    if (!normalized) {
      return this.getRecent();
    }

    const recent = this.getRecent().filter((stored) => stored.id !== normalized.id);
    const next = [normalized, ...recent].slice(0, LOCATION_SEARCH_HISTORY_LIMIT);
    this.writeRecent(next);

    return next;
  }

  private normalizeItem(item: unknown): LocationSearchHistoryItem | null {
    if (!item || typeof item !== 'object') {
      return null;
    }

    const candidate = item as Partial<LocationSearchHistoryItem>;
    const id = String(candidate.id ?? '').trim();
    const label = String(candidate.label ?? '').trim();
    const latitude = Number(candidate.latitude);
    const longitude = Number(candidate.longitude);

    if (!id || !label || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return null;
    }

    return {
      id,
      label,
      latitude,
      longitude,
    };
  }

  private writeRecent(items: LocationSearchHistoryItem[]): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(LOCATION_SEARCH_HISTORY_STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Local storage can be unavailable or full; search still works without history.
    }
  }
}
